/**
 * Service Principal Detail Page
 *
 * Feature: 023-service-principal-registry
 * Task: T017 - Create ServicePrincipalDetailPage with assignments list
 * Constitution: v1.19.0
 *
 * Conformed to the Spec 197 design blueprint — migrated the bespoke
 * `max-w-7xl` shell / raw-gray header / `bg-white … rounded … shadow`
 * cards / hand-rolled stat blocks / raw `<table>` onto the canonical
 * Tailwind+token UI kit (DetailLayout / Card / DescriptionList /
 * StatCard / DualScrollTable). Presentation-only; no data/logic change.
 *
 * Detail page for a single service principal with assignments and drifts
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ServicePrincipal,
  SpWorkspaceAssignment,
  UpdateServicePrincipalRequest,
  CreateSpWorkspaceAssignmentRequest,
  PermissionLevel,
} from '../types';
import {
  useServicePrincipalWithSummary,
  useUpdateServicePrincipal,
  useDeleteServicePrincipal,
  useCreateAssignment,
  useDeleteAssignment,
} from '../hooks/useServicePrincipals';
import { DriftStatusBadge } from '../components/DriftStatusBadge';
import { ServicePrincipalForm } from '../components/ServicePrincipalForm';
import { DualScrollTable, usePagination, PaginationControls } from '../../../components/common/TableControls';
import {
  Modal,
  Button,
  Card,
  DetailLayout,
  DescriptionList,
  StatCard,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

/**
 * Format date for display
 */
function formatDate(date: Date | string | null): string {
  if (!date) return 'Never';
  return formatEastern(date);
}

/**
 * Status badge colors (semantic status pills — left as-is per blueprint).
 */
const statusColors = {
  ACTIVE: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
  INACTIVE: 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  DELETED: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
};

/**
 * Category badge colors
 */
const categoryColors = {
  Backup: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  DataCollection: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  Monitoring: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  Integration: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
};

/**
 * Add Assignment Modal
 */
interface AddAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSpWorkspaceAssignmentRequest) => Promise<void>;
  isSubmitting: boolean;
}

const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [workspaceId, setWorkspaceId] = useState('');
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('Contributor');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      workspaceId: workspaceId.trim(),
      permissionLevel,
      expected: true,
    });
    setWorkspaceId('');
    setPermissionLevel('Contributor');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Add Workspace Assignment'
      size='sm'
      footer={
        <>
          <Button variant='outline' type='button' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='add-assignment-form'
            isLoading={isSubmitting}
            disabled={isSubmitting || !workspaceId.trim()}
          >
            {isSubmitting ? 'Adding...' : 'Add Assignment'}
          </Button>
        </>
      }
    >
      <form id='add-assignment-form' onSubmit={handleSubmit}>
        <div className='space-y-4'>
          <div>
            <label htmlFor='workspaceId' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
              Workspace ID
            </label>
            <input
              type='text'
              id='workspaceId'
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder='00000000-0000-0000-0000-000000000000'
              className='mt-1 block w-full border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md px-3 py-2'
              required
            />
          </div>
          <div>
            <label htmlFor='permissionLevel' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
              Permission Level
            </label>
            <select
              id='permissionLevel'
              value={permissionLevel}
              onChange={(e) => setPermissionLevel(e.target.value as PermissionLevel)}
              className='mt-1 block w-full border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md px-3 py-2'
            >
              <option value='Admin'>Admin</option>
              <option value='Member'>Member</option>
              <option value='Contributor'>Contributor</option>
              <option value='Viewer'>Viewer</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Service Principal Detail Page Component
 */
export const ServicePrincipalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [showAddAssignment, setShowAddAssignment] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { data: spData, isLoading, isFetching, error, refetch } = useServicePrincipalWithSummary(id || '');

  const updateMutation = useUpdateServicePrincipal();
  const deleteMutation = useDeleteServicePrincipal();
  const createAssignmentMutation = useCreateAssignment(id || '');
  const deleteAssignmentMutation = useDeleteAssignment(id || '');

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination<SpWorkspaceAssignment>(spData?.assignments || []);

  if (isLoading) {
    return (
      <DetailLayout title='Service Principal' backTo='/service-principals' backLabel='Back to Service Principals'>
        <Card>
          <LoadingState rows={6} />
        </Card>
      </DetailLayout>
    );
  }

  if (error || !spData) {
    return (
      <DetailLayout title='Service Principal' backTo='/service-principals' backLabel='Back to Service Principals'>
        <ErrorState
          title='Error loading service principal'
          message={error?.message || 'Service principal not found'}
          onRetry={() => refetch()}
        />
      </DetailLayout>
    );
  }

  const handleUpdateSubmit = async (data: UpdateServicePrincipalRequest) => {
    await updateMutation.mutateAsync({ id: id!, data });
    setIsEditing(false);
    refetch();
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(id!);
    navigate('/service-principals');
  };

  const handleAddAssignment = async (data: CreateSpWorkspaceAssignmentRequest) => {
    await createAssignmentMutation.mutateAsync(data);
    setShowAddAssignment(false);
    refetch();
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (window.confirm('Are you sure you want to remove this assignment?')) {
      await deleteAssignmentMutation.mutateAsync(assignmentId);
      refetch();
    }
  };

  return (
    <DetailLayout
      title={spData.displayName}
      subtitle={<span className='font-mono'>{spData.applicationId}</span>}
      backTo='/service-principals'
      backLabel='Back to Service Principals'
      helpId='internal.service-principal-detail'
      onRefresh={async () => { await refetch(); }}
      isRefreshing={isFetching}
      badges={
        <>
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColors[spData.status]}`}>
            {spData.status}
          </span>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${categoryColors[spData.category]}`}>
            {spData.category}
          </span>
        </>
      }
      actions={
        <>
          <Button variant='outline' onClick={() => setIsEditing(true)} disabled={spData.status === 'DELETED'}>
            ✏️ Edit
          </Button>
          <Button variant='danger' onClick={() => setDeleteConfirm(true)} disabled={spData.status === 'DELETED'}>
            🗑️ Delete
          </Button>
        </>
      }
    >
      {/* Details + Status Cards */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
        <Card padding='lg'>
          <h2 className='text-lg font-semibold text-[var(--color-text-primary)] mb-4'>Details</h2>
          <DescriptionList
            items={[
              { label: 'Application ID', value: <span className='font-mono'>{spData.applicationId}</span> },
              { label: 'Object ID', value: <span className='font-mono'>{spData.objectId}</span> },
              { label: 'Purpose', value: spData.purpose || '—' },
              {
                label: 'Key Vault Secret URI',
                value: <span className='font-mono break-all'>{spData.keyVaultSecretUri || '—'}</span>,
              },
              {
                label: 'Auto-Fix Enabled',
                value: (
                  <span className={spData.autoFixEnabled ? 'text-[var(--color-status-success)]' : 'text-[var(--color-text-secondary)]'}>
                    {spData.autoFixEnabled ? '✓ Yes' : '✗ No'}
                  </span>
                ),
              },
            ]}
          />
        </Card>

        <Card padding='lg'>
          <h2 className='text-lg font-semibold text-[var(--color-text-primary)] mb-4'>Status</h2>
          <DescriptionList
            items={[
              { label: 'Last Entra Verified', value: formatDate(spData.lastEntraVerifiedAt) },
              { label: 'Last Fabric Verified', value: formatDate(spData.lastFabricVerifiedAt) },
              {
                label: 'Consecutive Failures',
                value: (
                  <span className={`font-medium ${spData.consecutiveFailures > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-[var(--color-text-primary)]'}`}>
                    {spData.consecutiveFailures}
                  </span>
                ),
              },
              { label: 'Created', value: formatDate(spData.createdAt) },
              { label: 'Last Updated', value: `${formatDate(spData.updatedAt)} by ${spData.updatedBy}` },
            ]}
          />
        </Card>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        <StatCard label='Workspace Assignments' value={spData.assignmentCount} />
        <StatCard
          label='Active Drifts'
          value={spData.activeDriftCount}
          valueClassName={
            spData.activeDriftCount > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-[var(--color-status-success)]'
          }
        />
      </div>

      {/* Assignments Table */}
      <QuickGlance>
        <Card className='p-4 text-sm text-[var(--color-text-secondary)]'>
          Open on a larger screen for the full workspace-assignments table.
        </Card>
      </QuickGlance>
      <Extended>
      <Card padding='none'>
        <div className='p-4 border-b border-[var(--color-border)] flex items-center justify-between'>
          <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>Workspace Assignments</h2>
          <Button size='sm' onClick={() => setShowAddAssignment(true)} disabled={spData.status === 'DELETED'}>
            ➕ Add Assignment
          </Button>
        </div>

        {spData.assignments && spData.assignments.length > 0 ? (
          <DualScrollTable>
            <table className='min-w-full divide-y divide-[var(--color-border)]'>
              <thead className='bg-[var(--color-bg-subtle)]'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Workspace ID
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Permission
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Expected
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Drift Status
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Last Verified
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
                {pageItems.map((assignment: SpWorkspaceAssignment) => (
                  <tr key={assignment.id} className='hover:bg-[var(--color-bg-subtle)]'>
                    <td className='px-4 py-3 text-sm font-mono text-[var(--color-text-primary)]'>
                      {assignment.workspaceId}
                    </td>
                    <td className='px-4 py-3'>
                      <span className='px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'>
                        {assignment.permissionLevel}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      {assignment.expected ? (
                        <span className='text-[var(--color-status-success)]'>✓ Yes</span>
                      ) : (
                        <span className='text-[var(--color-text-secondary)]'>✗ No</span>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      <DriftStatusBadge status={assignment.driftStatus} size='sm' />
                    </td>
                    <td className='px-4 py-3 text-sm text-[var(--color-text-secondary)]'>
                      {formatDate(assignment.lastVerifiedAt)}
                    </td>
                    <td className='px-4 py-3'>
                      <button
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        disabled={deleteAssignmentMutation.isPending}
                        className='text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm'
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DualScrollTable>
        ) : (
          <div className='p-8 text-center'>
            <p className='text-[var(--color-text-secondary)]'>No workspace assignments configured</p>
            <button
              onClick={() => setShowAddAssignment(true)}
              className='mt-2 text-[var(--color-accent)] hover:underline text-sm'
            >
              Add the first assignment
            </button>
          </div>
        )}
        <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
      </Card>
      </Extended>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        title='Edit Service Principal'
        size='md'
      >
        <ServicePrincipalForm
          initialData={spData}
          onSubmit={handleUpdateSubmit}
          onCancel={() => setIsEditing(false)}
          isSubmitting={updateMutation.isPending}
          mode='edit'
        />
      </Modal>

      {/* Add Assignment Modal */}
      <AddAssignmentModal
        isOpen={showAddAssignment}
        onClose={() => setShowAddAssignment(false)}
        onSubmit={handleAddAssignment}
        isSubmitting={createAssignmentMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title='Delete Service Principal?'
        size='sm'
        footer={
          <>
            <Button variant='outline' onClick={() => setDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant='danger' onClick={handleDelete} isLoading={deleteMutation.isPending} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Are you sure you want to delete <strong>{spData.displayName}</strong>?
        </p>
      </Modal>
    </DetailLayout>
  );
};

export default ServicePrincipalDetailPage;
